/* =====================================================================================
   TravelPerk: the one first-class booking provider, and what "first-class" honestly means.

   THE VERIFIED API SURFACE (developers.perk.com, checked 27 August 2026). TravelPerk's
   public API, authenticated with OAuth 2.0, offers:

     * trips: read and filter, including by traveller
     * invoices: list and retrieve, including the invoice PDF itself
     * SCIM: user provisioning
     * cost centers and custom fields: read and manage
     * webhooks: subscribe to trip and invoice events

   There is NO booking-creation endpoint. That is TravelPerk's deliberate design, not a gap
   in ours: bookings are made by a person inside their app, where the traveller picks a
   seat and a fare. So the honest integration is a HANDOFF OUT and a SYNC BACK, and this
   module implements the first and scaffolds the second.

   THE TIER LINE (TravelPerk pricing, verified 27 August 2026): API and webhook access
   sits in their Pro tier (13 pounds per user per month at time of checking); the free
   tier is the booking interface only. So the deep-link handoff plus attaching
   confirmations and invoices by hand works on FREE, and everything under "sync back"
   below additionally requires PRO. The connection state copy says so, because "not
   configured" would otherwise read as our omission when it may be a deliberate
   subscription decision.

   THE HANDOFF (live in this build). An approved request offers "Book in TravelPerk": a
   deep link into the TravelPerk app plus the trip summary on the clipboard, so the person
   booking pastes what was approved rather than retyping it. The request moves to
   booked-pending-confirmation, exactly as it would with an email channel.

   THE SYNC BACK (scaffolded, deliberately inert). Once a tenant has a TravelPerk account
   and an OAuth application, booking data can flow back without anybody forwarding PDFs:

     1. CONNECTION. The tenant's file carries clientId and the NAME of the Supabase secret
        holding the client secret (TravelPerkConnection in tenants/types.ts). The secret
        itself never enters a tenant file: tenant files compile into a public bundle.

     2. WEBHOOK RECEIVER, designed, not yet deployed. A Supabase edge function in the
        TENANT'S OWN project (each tenant has its own; a shared receiver would be a
        cross-tenant channel). Sketch of `travelperk-webhook`:
          - POST endpoint registered with TravelPerk's webhooks API;
          - verifies the delivery is genuine before reading it;
          - on a trip event, finds the Forum request by looking for the TRV reference in
            the trip's custom fields (the person booking pastes the copied summary, whose
            first line carries the reference; a custom field named "Forum reference" makes
            the match exact rather than textual);
          - on an invoice event, fetches the invoice PDF over the API with the OAuth
            client credentials, uploads it to the 'general' bucket under
            {org}/travel_request/{id}/, and inserts the app.attachments row with a
            description like "TravelPerk invoice 2026-0142, Vienna congress trip", which
            is the same two-phase write uploadFile() does and must keep its order;
          - moves booking_requested to booked when the trip confirms.

     3. INVOICE ATTACHMENT MAPPING. TravelPerk invoice -> app.attachments with
        object_type 'travel_request', bucket 'general', role 'evidence', description from
        the invoice number and trip name. Nothing new is invented: the 0093 description
        column and the 0094 object_type are the whole mechanism.

   Until all three exist the UI says "connection not configured" and the paper trail is
   the traveller attaching confirmations by hand, which works today and keeps working
   after, as the fallback.
   ===================================================================================== */

import type { TravelBookingChannel } from '../tenants/types';

export const TRAVELPERK_APP_URL = 'https://app.travelperk.com';
export const TRAVELPERK_API_BASE = 'https://api.travelperk.com';

/* 'unconfigured': no OAuth client id declared, the normal state; the handoff still works.
   'scaffolded': a client id is declared but this build makes no live calls, so the sync
   back is described, not running. There is deliberately no 'live' state to claim yet. */
export type TravelPerkConnectionState = 'unconfigured' | 'scaffolded';

export function travelPerkConnectionState(ch: TravelBookingChannel): TravelPerkConnectionState {
  return ch.connection?.clientId ? 'scaffolded' : 'unconfigured';
}

export function travelPerkAppUrl(ch: TravelBookingChannel): string {
  return ch.appUrl || TRAVELPERK_APP_URL;
}

/** The sentence the Travel page shows under a TravelPerk channel, kept here so the module
    never has to know what the states mean. */
export function describeTravelPerkConnection(ch: TravelBookingChannel): string {
  return travelPerkConnectionState(ch) === 'scaffolded'
    ? 'Sync-back connection declared but not live in this build: itineraries and invoices '
      + 'still come back by attachment. Going live needs a TravelPerk Pro subscription, '
      + 'which is where their API and webhooks live.'
    : 'Automated sync (booking status and invoice ingestion) needs a TravelPerk Pro '
      + 'subscription and is not configured. Booking through the app and attaching the '
      + 'confirmation and invoice here works on the free tier, and is the current flow.';
}

/* -------------------------------------------------------------------------------------
   Documented stubs. They exist so the call sites that will need them are typed and
   discoverable now, and they throw the sentence a person should read, because a stub
   that silently returns [] would teach the UI to render an empty sync as a working one.
   ------------------------------------------------------------------------------------- */

/** Would list the tenant's TravelPerk trips, filtered by traveller, over the OAuth
    connection. Requires the connection and the edge-function proxy; see the header. */
export async function fetchTravelPerkTrips(): Promise<never> {
  throw new Error('TravelPerk sync is not configured. Trips are recorded here when the '
    + 'traveller hands a request to the channel; itineraries attach to the request.');
}

/** Would fetch a TravelPerk invoice PDF and file it against the request through the
    attachments registry. Requires the connection; the mapping is in the header. */
export async function attachTravelPerkInvoices(): Promise<never> {
  throw new Error('TravelPerk sync is not configured. Attach the invoice to the request '
    + 'directly; the description names it for the next person.');
}
